import { useState, useRef, useEffect } from "react";
import { Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Outlet, Navigate } from "react-router-dom";
import { Logo, LogoIcon } from "@/components/Logo";
import { cn } from "@/lib/utils";
import { generateUUID } from "@/utils/transform";
import { useAuthStore } from "../../store/auth.store";
import { ThemeToggle } from "../ThemeToggle";
import { Card } from "../ui/card";

// Video player component for the right side
const VideoPanel = () => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // React does not reflect the `muted` prop into the DOM attribute, so
      // Safari parses the video as unmuted, blocks autoplay, and shows its
      // native play controls. Mute via the property before calling play().
      video.defaultMuted = true;
      video.muted = true;
      video.play().catch(() => {
        // Autoplay was prevented, that's okay
      });
    }
  }, []);

  return (
    <div className="relative h-full w-full overflow-hidden bg-linear-to-br from-primary from-45% via-primary/50 via-80% to-primary/10 to-99%">
      {/* Video background - wrapped in a div for Safari compositing */}
      <div
        className={cn(
          // z-0 pins the composited video layer below the gradient overlays:
          // Safari paints transformed (translateZ) layers above z-auto siblings
          "absolute inset-0 z-0 transition-opacity duration-700",
          isLoaded ? "opacity-40" : "opacity-0",
        )}
        style={{
          // Force GPU compositing layer for Safari
          transform: "translateZ(0)",
          WebkitTransform: "translateZ(0)",
        }}
      >
        <video
          ref={videoRef}
          aria-label={t("auth.video.ariaLabel", "Intro Video")}
          className="w-full h-full object-cover pointer-events-none"
          poster="/videos/intro-poster.jpg"
          loop
          muted
          playsInline
          autoPlay
          disablePictureInPicture
          onLoadedData={() => setIsLoaded(true)}
          // Prevent black background on iOS Safari
          style={{
            backgroundColor: "transparent",
            WebkitBackfaceVisibility: "hidden",
          }}
        >
          {/* Only reference sources that actually ship. In production,
              CloudFront's SPA fallback turns missing files into 200-with-HTML
              responses; Safari picks the HEVC .mov source first, receives
              HTML, and fails without falling through to the mp4. If .mov/.webm
              variants are added to public/videos later, restore their
              <source> entries. */}
          <source src="/videos/intro.mp4" type="video/mp4" />
        </video>
      </div>

      {/* Gradient overlay: explicit z-index so Safari stacks it above the
          composited video layer */}
      <div className="absolute inset-0 z-[1] pointer-events-none bg-linear-to-t from-primary/90 via-primary/50 to-transparent" />
      <div className="absolute inset-0 z-[1] pointer-events-none bg-linear-to-r from-primary/60 to-transparent" />

      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      {/* Content overlay */}
      <div className="relative z-10 flex flex-col justify-between h-full p-8 lg:p-12">
        {/* Top section */}

        {/* Center content */}
        <div className="flex-1 flex flex-col justify-center max-w-lg">
          <h2 className="text-4xl lg:text-5xl font-bold font-heading text-white leading-tight mb-6">
            {t("auth.video.headline")}
          </h2>
          <p className="text-lg text-white/80 leading-relaxed mb-8">
            {t("auth.video.subheadline")}
          </p>

          {/* Feature highlights */}
          <div className="space-y-4">
            {[
              {
                label: t("auth.video.feature1"),
              },
              {
                label: t("auth.video.feature2"),
              },
              {
                label: t("auth.video.feature3"),
              },
            ].map((feature, index) => (
              <div
                key={generateUUID()}
                className="flex items-center gap-3 text-white/90"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-2 h-2 rounded-full bg-white/60" />
                <span className="text-sm font-medium">{feature.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom section */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-white/60">{t("auth.video.trusted")}</p>

          {/* Trust badges */}
          <div className="flex items-center gap-4">
            {/* <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full">
              <Shield className="w-3.5 h-3.5 text-white/80" />
              <span className="text-xs font-medium text-white/80">{t('compliance.soc2')}</span>
            </div> */}
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full">
              <span className="text-xs font-medium text-white/80">
                {t("settings.gdpr")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder when video is not available
const VideoPlaceholder = () => {
  const { t } = useTranslation();

  return (
    <div className="relative h-full w-full overflow-hidden bg-linear-to-br from-primary via-primary/90 to-primary/80">
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='https://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
      <div className="absolute top-1/2 left-1/2 w-125 h-125 bg-white/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />

      {/* Content */}
      <div className="relative z-10 flex flex-col justify-between h-full p-8 lg:p-12">
        {/* Top section */}
        <div className="flex items-center gap-3 text-white/90">
          <LogoIcon size="lg" surface="dark" className="!size-10" />
          <span className="text-lg font-semibold tracking-tight">
            {t("common.appName")}
          </span>
        </div>

        {/* Center content */}
        <div className="flex-1 flex flex-col justify-center max-w-lg">
          <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            {t("auth.video.headline")}
          </h2>
          <p className="text-lg text-white/80 leading-relaxed mb-8">
            {t("auth.video.subheadline")}
          </p>

          {/* Feature highlights */}
          <div className="space-y-4">
            {[
              {
                label: t("auth.video.feature1"),
              },
              {
                label: t("auth.video.feature2"),
              },
              {
                label: t("auth.video.feature3"),
              },
            ].map((feature) => (
              <div
                key={generateUUID()}
                className="flex items-center gap-3 text-white/90"
              >
                <div className="w-2 h-2 rounded-full bg-white/60" />
                <span className="text-sm font-medium">{feature.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom section */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-white/60">{t("auth.video.trusted")}</p>

          {/* Trust badges */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full">
              <Shield className="w-3.5 h-3.5 text-white/80" />
              <span className="text-xs font-medium text-white/80">
                {t("compliance.soc2")}
              </span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full">
              <span className="text-xs font-medium text-white/80">
                {t("settings.gdpr")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AuthLayout = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const [hasVideo, setHasVideo] = useState(false);

  // Check if any video format exists
  useEffect(() => {
    const checkVideo = async () => {
      const formats = ["/videos/intro.mp4"];
      for (const url of formats) {
        try {
          const response = await fetch(url, { method: "HEAD" });
          // Check content-type too: the production CDN serves the SPA's
          // index.html with a 200 for missing files, which would count a
          // missing video as present
          const type = response.headers.get("content-type") || "";
          if (response.ok && type.startsWith("video/")) {
            setHasVideo(true);
            return;
          }
        } catch {
          // Continue to next format
        }
      }
      setHasVideo(false);
    };
    checkVideo();
  }, []);

  // Redirect to dashboard if already logged in
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 min-h-screen flex flex-col bg-background">
        {/* Header */}
        <div className="flex items-center justify-between p-4 lg:p-6">
          {/* Logo - visible on mobile and desktop */}
          <div className="flex items-center gap-2 lg:hidden">
            <Logo size="md" showText text={t("common.appName")} />
          </div>

          {/* Desktop logo */}
          <div className="hidden lg:flex items-center gap-2">
            <Logo size="lg" showText text={t("common.appName")} />
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            <ThemeToggle />
          </div>
        </div>

        {/* Form container */}
        <div className="flex-1 flex items-center justify-center px-4 py-8 lg:px-8">
          <div className="w-full max-w-md">
            <Card className="p-6 sm:p-8 shadow-lg border-border/50">
              <Outlet />
            </Card>

            {/* Footer */}
            <p className="mt-6 text-center text-sm text-muted-foreground">
              {t("common.tagline")}
            </p>
            {/* <div className="mt-2 flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <Link
                to="/terms"
                target="_blank"
                className="hover:text-foreground"
              >
                {t("auth.register.termsOfService")}
              </Link>
              <span aria-hidden="true">·</span>
              <Link
                to="/privacy"
                target="_blank"
                className="hover:text-foreground"
              >
                {t("auth.register.privacyPolicy")}
              </Link>
            </div>*/}
          </div>
        </div>

        {/* Bottom padding for mobile */}
        <div className="h-4 lg:h-6" />
      </div>

      {/* Right side - Video/Branding (hidden on mobile) */}
      <div className="hidden lg:block w-1/2 min-h-screen sticky top-0">
        {hasVideo ? <VideoPanel /> : <VideoPlaceholder />}
      </div>
    </div>
  );
};

export default AuthLayout;
